/**
 * DraggableStyleList Component
 * 
 * Allows drag-and-drop reordering of style configuration items.
 * Uses native HTML5 drag and drop API for lightweight implementation.
 * Supports show/hide toggle for each item.
 */

import React, { useState, useCallback } from 'react';
import { GripVertical, Eye, EyeOff } from 'lucide-react';
import { StyleSelector, StyleSelectorProps } from './StyleSelector';

export interface DraggableItem {
  id: string;
  label: string;
  options: { id: string; name: string; description?: string }[];
  value: string;
  onChange: (value: string) => void;
  visible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export interface DraggableStyleListProps {
  items: DraggableItem[];
  onReorder: (newOrder: string[]) => void;
  disabled?: boolean;
  title?: string;
  showVisibilityToggle?: boolean;
}

export const DraggableStyleList: React.FC<DraggableStyleListProps> = ({
  items,
  onReorder,
  disabled = false,
  title,
  showVisibilityToggle = false
}) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    if (disabled) return;
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    // Add drag image styling
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  }, [disabled]);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    setDraggedId(null);
    setDragOverId(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== draggedId) {
      setDragOverId(id);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    
    if (sourceId === targetId) {
      setDragOverId(null);
      return;
    }

    const currentOrder = items.map(item => item.id);
    const sourceIndex = currentOrder.indexOf(sourceId);
    const targetIndex = currentOrder.indexOf(targetId);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDragOverId(null);
      return;
    }

    // Reorder array
    const newOrder = [...currentOrder];
    newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, sourceId);

    onReorder(newOrder);
    setDragOverId(null);
  }, [items, onReorder]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {title && (
        <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
          <GripVertical className="w-3 h-3" />
          <span>拖動調整順序</span>
        </div>
      )}
      {items.map((item) => {
        const isVisible = item.visible !== false;
        return (
          <div
            key={item.id}
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(e, item.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, item.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, item.id)}
            className={`
              relative rounded-lg transition-all duration-150
              ${draggedId === item.id ? 'opacity-50' : ''}
              ${dragOverId === item.id ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-slate-900' : ''}
              ${!disabled ? 'cursor-grab active:cursor-grabbing' : ''}
              ${!isVisible ? 'opacity-50' : ''}
            `}
          >
            <div className="flex items-start gap-2">
              {/* Drag Handle */}
              <div className={`
                flex-shrink-0 pt-1 text-slate-600 
                ${!disabled ? 'hover:text-slate-400' : 'opacity-50'}
              `}>
                <GripVertical className="w-4 h-4" />
              </div>
              
              {/* Style Selector */}
              <div className="flex-1">
                <StyleSelector
                  label={item.label}
                  options={item.options}
                  value={item.value}
                  onChange={item.onChange}
                  disabled={disabled || !isVisible}
                />
              </div>

              {/* Visibility Toggle */}
              {showVisibilityToggle && item.onVisibilityChange && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onVisibilityChange?.(!isVisible);
                  }}
                  className={`
                    flex-shrink-0 p-1.5 rounded-lg transition-colors
                    ${isVisible 
                      ? 'text-blue-400 hover:bg-blue-500/20' 
                      : 'text-slate-500 hover:bg-slate-700/50'
                    }
                  `}
                  title={isVisible ? '隱藏' : '顯示'}
                >
                  {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DraggableStyleList;
